// ============================================================
// ExpensesTab.jsx  —  Noble Enterprises (tape module)
//
// WHAT'S ON THIS SCREEN
//   • Date range + category filter
//   • Totals, and the same totals split by category
//   • Entry list with inline add / edit
//
// TWO KINDS OF ROW
// Manual entries are typed here. Rows with source='hr' were
// posted by the HR module when a salary was allocated to this
// business, and are read-only — editing them here would put
// the two modules out of step. They are shown because they are
// a real cost of running the tape business and the P&L would
// understate without them.
//
// WHY THE CATEGORY SPLIT IS ALWAYS VISIBLE
// One total tells you what was spent. The split tells you
// whether that is normal. Electricity doubling matters; the
// combined figure hides it.
// ============================================================

import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Plus, Pencil, Trash2, X, Check, Lock } from 'lucide-react';
import { DEFAULT_FROM_DATE, todayISO } from '../components/WindowModal';

const API = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const C = {
  brand:      '#f39c12',
  text:       '#1a2332',
  textSoft:   '#64748b',
  textMuted:  '#94a3b8',
  border:     '#e2e8f0',
  borderSoft: '#f1f5f9',
  card:       '#ffffff',
  rowAlt:     '#fafbfc',
  danger:     '#dc2626',
  dangerBg:   '#fee2e2',
  success:    '#059669',
  successBg:  '#d1fae5',
  info:       '#0284c7',
  infoBg:     '#e0f2fe',
};

const money = (n) =>
  `Rs ${Number(n || 0).toLocaleString('en-PK', { maximumFractionDigits: 0 })}`;
const fmtDate = (d) => new Date(d).toLocaleDateString('en-GB');


// ============================================================
// SHARED UI
// ============================================================

const inputStyle = {
  width: '100%', padding: '10px 13px',
  border: `1px solid ${C.border}`, borderRadius: '8px',
  fontSize: '14px', color: C.text, boxSizing: 'border-box',
  fontFamily: 'inherit', background: '#ffffff',
};

const Field = ({ label, required, children }) => (
  <div>
    <label style={{
      display: 'block', fontSize: '12px', fontWeight: '600',
      color: C.textSoft, marginBottom: '6px',
    }}>
      {label}
      {required && <span style={{ color: C.danger, marginLeft: '3px' }}>*</span>}
    </label>
    {children}
  </div>
);

const TextInput = (props) => <input style={inputStyle} {...props} />;

const Btn = ({ variant = 'primary', children, ...props }) => {
  const styles = {
    primary: { background: C.brand,   color: '#ffffff',  border: 'none' },
    ghost:   { background: '#ffffff', color: C.textSoft, border: `1px solid ${C.border}` },
  };
  return (
    <button
      style={{
        ...styles[variant],
        padding: '10px 18px', borderRadius: '8px', fontSize: '14px',
        fontWeight: '600', cursor: 'pointer', fontFamily: 'inherit',
        display: 'inline-flex', alignItems: 'center', gap: '7px',
      }}
      {...props}
    >
      {children}
    </button>
  );
};

const IconBtn = ({ danger, disabled, title, children, ...props }) => (
  <button
    title={title}
    disabled={disabled}
    style={{
      width: '32px', height: '32px', borderRadius: '7px',
      border: `1px solid ${C.border}`, background: '#ffffff',
      color: disabled ? C.textMuted : (danger ? C.danger : C.textSoft),
      cursor: disabled ? 'not-allowed' : 'pointer',
      opacity: disabled ? 0.45 : 1,
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    }}
    {...props}
  >
    {children}
  </button>
);

const Banner = ({ type, message, onClose }) => {
  if (!message) return null;
  const isError = type === 'error';
  return (
    <div style={{
      background: isError ? C.dangerBg : C.successBg,
      color:      isError ? C.danger   : C.success,
      padding: '11px 16px', borderRadius: '8px', marginBottom: '16px',
      fontSize: '14px', display: 'flex', alignItems: 'center', gap: '10px',
    }}>
      <span style={{ flex: 1 }}>{message}</span>
      <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'inherit', display: 'flex' }}>
        <X size={16} />
      </button>
    </div>
  );
};

const thStyle = {
  textAlign: 'left', padding: '11px 20px', fontSize: '12px',
  fontWeight: '600', color: C.textSoft, background: C.rowAlt,
  textTransform: 'uppercase', letterSpacing: '0.3px',
};
const tdStyle = {
  padding: '13px 20px', fontSize: '14px', color: C.text,
  borderTop: `1px solid ${C.borderSoft}`,
};


// ============================================================
// EXPENSE FORM
// ============================================================

const emptyForm = {
  id: null,
  category_id: '',
  expense_date: todayISO(),
  amount: '',
  paid_to: '',
  ref_no: '',
  notes: '',
};

const ExpenseForm = ({ form, setForm, categories, onSave, onCancel, saving }) => (
  <div style={{
    padding: '20px 22px', background: C.rowAlt,
    borderBottom: `1px solid ${C.borderSoft}`,
  }}>
    <div style={{
      display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))',
      gap: '16px', marginBottom: '18px',
    }}>
      <Field label="Category" required>
        <select
          style={inputStyle}
          value={form.category_id}
          onChange={e => setForm({ ...form, category_id: e.target.value })}
        >
          <option value="">Select…</option>
          {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
      </Field>

      <Field label="Date" required>
        <TextInput
          type="date"
          max={todayISO()}
          value={form.expense_date}
          onChange={e => setForm({ ...form, expense_date: e.target.value })}
        />
      </Field>

      <Field label="Amount" required>
        <TextInput
          type="number" step="0.01" min="0"
          value={form.amount}
          onChange={e => setForm({ ...form, amount: e.target.value })}
          placeholder="0"
        />
      </Field>

      <Field label="Paid to">
        <TextInput
          value={form.paid_to}
          onChange={e => setForm({ ...form, paid_to: e.target.value })}
          placeholder="Who was paid"
        />
      </Field>

      <Field label="Reference">
        <TextInput
          value={form.ref_no}
          onChange={e => setForm({ ...form, ref_no: e.target.value })}
          placeholder="Bill / cheque no."
        />
      </Field>

      <Field label="Notes">
        <TextInput
          value={form.notes}
          onChange={e => setForm({ ...form, notes: e.target.value })}
          placeholder="Optional"
        />
      </Field>
    </div>

    <div style={{ display: 'flex', gap: '10px' }}>
      <Btn onClick={onSave} disabled={saving}>
        <Check size={16} /> {saving ? 'Saving…' : (form.id ? 'Update expense' : 'Save expense')}
      </Btn>
      <Btn variant="ghost" onClick={onCancel}>Cancel</Btn>
    </div>
  </div>
);


// ============================================================
// MAIN COMPONENT
// ============================================================

const ExpensesTab = () => {
  const [data, setData] = useState({ rows: [], totals: {}, by_category: [] });
  const [categories, setCategories] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [form, setForm]         = useState(null);
  const [saving, setSaving]     = useState(false);
  const [banner, setBanner]     = useState({ type: '', message: '' });

  const [filters, setFilters] = useState({
    from_date:   DEFAULT_FROM_DATE,
    to_date:     todayISO(),
    category_id: '',
  });

  const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };

  const notify = (type, message) => {
    setBanner({ type, message });
    if (type === 'success') setTimeout(() => setBanner({ type: '', message: '' }), 3000);
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.from_date)   params.append('from_date', filters.from_date);
      if (filters.to_date)     params.append('to_date', filters.to_date);
      if (filters.category_id) params.append('category_id', filters.category_id);

      const [e, c] = await Promise.all([
        axios.get(`${API}/tape/finance/expenses?${params}`, { headers }),
        axios.get(`${API}/tape/finance/categories`, { headers }),
      ]);

      setData(e.data.data || { rows: [], totals: {}, by_category: [] });
      setCategories(c.data.data || []);
    } catch {
      notify('error', 'Could not load expenses. Is the backend running?');
    } finally {
      setLoading(false);
    }
  }, [filters]); // eslint-disable-line

  useEffect(() => { load(); }, [load]);

  const openAdd = () => setForm(emptyForm);

  const openEdit = (row) => {
    if (row.source !== 'manual') {
      return notify('error', 'This entry was posted from HR. Edit it in the HR module.');
    }
    setForm({
      id: row.id,
      category_id: String(row.category_id),
      expense_date: String(row.expense_date).split('T')[0],
      amount: row.amount,
      paid_to: row.paid_to || '',
      ref_no: row.ref_no || '',
      notes: row.notes || '',
    });
  };

  const save = async () => {
    const f = form;
    if (!f.category_id)          return notify('error', 'Pick a category');
    if (!f.expense_date)         return notify('error', 'Date is required');
    if (!(Number(f.amount) > 0)) return notify('error', 'Amount must be greater than 0');

    setSaving(true);
    try {
      const payload = {
        category_id:  f.category_id,
        expense_date: f.expense_date,
        amount:       f.amount,
        paid_to:      f.paid_to,
        ref_no:       f.ref_no,
        notes:        f.notes,
      };

      if (f.id) {
        await axios.put(`${API}/tape/finance/expenses/${f.id}`, payload, { headers });
        notify('success', 'Expense updated');
      } else {
        await axios.post(`${API}/tape/finance/expenses`, payload, { headers });
        notify('success', 'Expense recorded');
      }

      setForm(null);
      load();
    } catch (err) {
      notify('error', err.response?.data?.message || 'Could not save');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (row) => {
    if (!window.confirm(`Delete this ${money(row.amount)} expense?`)) return;
    try {
      await axios.delete(`${API}/tape/finance/expenses/${row.id}`, { headers });
      notify('success', 'Expense deleted');
      load();
    } catch (err) {
      notify('error', err.response?.data?.message || 'Could not delete');
    }
  };

  const total   = Number(data.totals?.total || 0);
  const entries = Number(data.totals?.entry_count || 0);
  const biggest = data.by_category?.[0];

  return (
    <div>
      <Banner
        type={banner.type}
        message={banner.message}
        onClose={() => setBanner({ type: '', message: '' })}
      />

      {/* Headline */}
      <div style={{
        display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))',
        gap: '14px', marginBottom: '16px',
      }}>
        {[
          ['Total spent', money(total)],
          ['Entries', entries],
          ['Biggest category', biggest ? biggest.category_name : '—'],
          ['That category', biggest ? money(biggest.total) : '—'],
        ].map(([label, value]) => (
          <div key={label} style={{
            background: C.card, border: `1px solid ${C.border}`,
            borderRadius: '10px', padding: '16px 18px',
          }}>
            <div style={{ fontSize: '11px', color: C.textMuted, fontWeight: '600', textTransform: 'uppercase' }}>
              {label}
            </div>
            <div style={{ fontSize: '20px', fontWeight: '700', color: C.text, marginTop: '4px' }}>
              {value}
            </div>
          </div>
        ))}
      </div>

      {/* Category split — a bar per category, widest first */}
      {data.by_category?.length > 0 && (
        <div style={{
          background: C.card, border: `1px solid ${C.border}`,
          borderRadius: '12px', padding: '18px 22px', marginBottom: '16px',
        }}>
          <div style={{
            fontSize: '12px', fontWeight: '600', color: C.textSoft,
            textTransform: 'uppercase', letterSpacing: '0.3px', marginBottom: '14px',
          }}>
            Where it went
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '11px' }}>
            {data.by_category.map(cat => {
              const pct = total > 0 ? (Number(cat.total) / total) * 100 : 0;
              return (
                <div key={cat.category_name}>
                  <div style={{
                    display: 'flex', justifyContent: 'space-between',
                    fontSize: '13px', marginBottom: '5px',
                  }}>
                    <span style={{ color: C.text, fontWeight: '600' }}>
                      {cat.category_name}
                      <span style={{ color: C.textMuted, fontWeight: '400', marginLeft: '7px' }}>
                        {cat.entry_count} {cat.entry_count === 1 ? 'entry' : 'entries'}
                      </span>
                    </span>
                    <span style={{ color: C.text, fontWeight: '600' }}>
                      {money(cat.total)}
                      <span style={{ color: C.textMuted, fontWeight: '400', marginLeft: '7px' }}>
                        {pct.toFixed(0)}%
                      </span>
                    </span>
                  </div>
                  <div style={{
                    height: '7px', borderRadius: '4px',
                    background: C.borderSoft, overflow: 'hidden',
                  }}>
                    <div style={{
                      width: `${pct}%`, height: '100%',
                      background: C.brand, borderRadius: '4px',
                      transition: 'width 0.2s',
                    }} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* List */}
      <div style={{
        background: C.card, border: `1px solid ${C.border}`,
        borderRadius: '12px', overflow: 'hidden',
      }}>
        <div style={{
          padding: '18px 22px', borderBottom: `1px solid ${C.borderSoft}`,
          display: 'flex', alignItems: 'flex-end', gap: '12px', flexWrap: 'wrap',
        }}>
          <div style={{ flex: '1 1 180px', minWidth: '160px' }}>
            <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '700', color: C.text }}>
              Expenses
            </h3>
            <p style={{ margin: '3px 0 0', fontSize: '13px', color: C.textSoft }}>
              Running costs. Salaries posted from HR appear here too.
            </p>
          </div>

          <div style={{ width: '150px' }}>
            <Field label="From">
              <TextInput
                type="date"
                value={filters.from_date}
                onChange={e => setFilters({ ...filters, from_date: e.target.value })}
              />
            </Field>
          </div>

          <div style={{ width: '150px' }}>
            <Field label="To">
              <TextInput
                type="date"
                value={filters.to_date}
                onChange={e => setFilters({ ...filters, to_date: e.target.value })}
              />
            </Field>
          </div>

          <div style={{ width: '175px' }}>
            <Field label="Category">
              <select
                style={inputStyle}
                value={filters.category_id}
                onChange={e => setFilters({ ...filters, category_id: e.target.value })}
              >
                <option value="">All categories</option>
                {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </Field>
          </div>

          {!form && (
            <Btn onClick={openAdd}><Plus size={16} /> Add expense</Btn>
          )}
        </div>

        {form && (
          <ExpenseForm
            form={form}
            setForm={setForm}
            categories={categories}
            onSave={save}
            onCancel={() => setForm(null)}
            saving={saving}
          />
        )}

        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '780px' }}>
            <thead>
              <tr>
                <th style={thStyle}>Date</th>
                <th style={thStyle}>Category</th>
                <th style={thStyle}>Paid to</th>
                <th style={thStyle}>Reference</th>
                <th style={thStyle}>Notes</th>
                <th style={{ ...thStyle, textAlign: 'right' }}>Amount</th>
                <th style={{ ...thStyle, textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td colSpan="7" style={{ ...tdStyle, textAlign: 'center', color: C.textMuted, padding: '36px' }}>
                    Loading…
                  </td>
                </tr>
              )}

              {!loading && data.rows.map(row => {
                const fromHr = row.source !== 'manual';
                return (
                  <tr key={row.id} style={{ background: fromHr ? C.rowAlt : 'transparent' }}>
                    <td style={{ ...tdStyle, color: C.textSoft, whiteSpace: 'nowrap' }}>
                      {fmtDate(row.expense_date)}
                    </td>
                    <td style={{ ...tdStyle, fontWeight: '600' }}>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: '7px' }}>
                        {row.category_name}
                        {fromHr && (
                          <span
                            title="Posted from the HR module"
                            style={{
                              display: 'inline-flex', alignItems: 'center', gap: '4px',
                              fontSize: '11px', fontWeight: '600',
                              padding: '2px 8px', borderRadius: '10px',
                              background: C.infoBg, color: C.info,
                            }}
                          >
                            <Lock size={10} /> HR
                          </span>
                        )}
                      </span>
                    </td>
                    <td style={tdStyle}>{row.paid_to || '—'}</td>
                    <td style={{ ...tdStyle, color: C.textSoft }}>{row.ref_no || '—'}</td>
                    <td style={{
                      ...tdStyle, color: C.textSoft, maxWidth: '220px',
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    }}>
                      {row.notes || '—'}
                    </td>
                    <td style={{ ...tdStyle, textAlign: 'right', fontWeight: '700' }}>
                      {money(row.amount)}
                    </td>
                    <td style={{ ...tdStyle, textAlign: 'right' }}>
                      <div style={{ display: 'inline-flex', gap: '7px' }}>
                        <IconBtn
                          title={fromHr ? 'Posted from HR — edit it there' : 'Edit'}
                          disabled={fromHr}
                          onClick={() => openEdit(row)}
                        >
                          <Pencil size={15} />
                        </IconBtn>
                        <IconBtn
                          danger
                          title={fromHr ? 'Posted from HR — reverse it there' : 'Delete'}
                          disabled={fromHr}
                          onClick={() => remove(row)}
                        >
                          <Trash2 size={15} />
                        </IconBtn>
                      </div>
                    </td>
                  </tr>
                );
              })}

              {!loading && data.rows.length === 0 && (
                <tr>
                  <td colSpan="7" style={{ ...tdStyle, textAlign: 'center', color: C.textMuted, padding: '40px' }}>
                    No expenses in this period.
                  </td>
                </tr>
              )}
            </tbody>

            {!loading && data.rows.length > 0 && (
              <tfoot>
                <tr>
                  <td colSpan="5" style={{
                    ...tdStyle, textAlign: 'right', fontWeight: '600',
                    color: C.textSoft, background: C.rowAlt,
                  }}>
                    Total
                  </td>
                  <td style={{
                    ...tdStyle, textAlign: 'right', fontWeight: '700',
                    fontSize: '15px', background: C.rowAlt,
                  }}>
                    {money(total)}
                  </td>
                  <td style={{ ...tdStyle, background: C.rowAlt }} />
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>
    </div>
  );
};

export default ExpensesTab;